import { Component, OnInit } from "@angular/core";

import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "src/app/_app-core/services/auth/auth.service";
import { AuthenticationService } from "src/app/_app-core/services/authenticationService";
import { JswVmdService } from "src/app/_app-core/services/jsw-vmd/jsw-vmd.service";
import { parseImpersonationToken } from "src/app/_app-core/impersonation-upload";

@Component({
    selector: "app-impersonate",
    templateUrl: "./impersonate.component.html",
    styleUrls: ["./impersonate.component.scss"],
})
export class ImpersonateComponent implements OnInit {
    sessionId: any;
    code: any;
    adminName: any;
    adminEmail: any;
    responseData;
    sellerCode: any;
    username: any;
    sessionTimeout: any;
    expiryDate: any;
    lastLoginDate: any;
    // GPXUSAP-1860 fix-round-1: the landing hint is a SEPARATE `landing` query
    // param (`#/manage-user?token=<5-segment token>&landing=purchase-orders`),
    // NOT part of the `$$$`-joined token. It used to be parsed as an optional
    // trailing 6th token segment, but that collided with the token's real
    // index-5 field (`username`, see below) — a 6-segment token would have
    // posted `username: "purchase-orders"` to the impersonate API. Only the
    // known value `purchase-orders` is honoured; anything else (absent,
    // unrecognised, or arbitrary attacker-controlled input) falls back to the
    // default `/dashboard`, exactly as today — see ngOnInit below.
    landing: string | undefined;
    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private authenticationService: AuthenticationService,
        private jswVmd: JswVmdService
    ) {
        this.route.queryParams.subscribe((params) => {
            if (params["token"]) {
                const pathParams = params["token"];
                // First five identity fields come from the single, unit-tested
                // source of truth (`_app-core/impersonation-upload.ts`) instead
                // of re-deriving positional parsing here.
                const parsed = parseImpersonationToken(pathParams);
                this.sessionId = parsed.sessionId;
                this.code = parsed.vendorCode;
                this.adminName = parsed.adminUser;
                this.adminEmail = parsed.adminEmail;
                this.sellerCode = parsed.sellerCode;
                // Fields beyond index 4 are session-establishment data, not
                // identity — outside parseImpersonationToken's contract, kept
                // as the existing positional reads.
                let pathSplit = pathParams.split("$$$");
                this.username = pathSplit[5];
                this.sessionTimeout = pathSplit[6];
                this.expiryDate = pathSplit[7];
                this.lastLoginDate = pathSplit[8];
                this.landing =
                    params["landing"] === "purchase-orders"
                        ? "purchase-orders"
                        : undefined;
            } else {
                this.router.navigateByUrl("/");
            }
        });
    }

    private saveToken(
        eippSession: string,
        sessionId: string,
        name: string,
        vendor: string,
        sellerCode: string,
        nonPo_flag: string,
        logistics_flag: string,
        contact: string | number,
        vendorEmail: string,
        adminUploadOnBehalf?: boolean
    ) {
        localStorage.setItem("eippSession", eippSession);
        localStorage.setItem("sessionId", sessionId);
        localStorage.setItem("displayName", name);
        localStorage.setItem("vendorCode", vendor);
        localStorage.setItem("contact", String(contact ?? ""));
        localStorage.setItem("sellerCode", sellerCode);
        localStorage.setItem("logisticFlag", logistics_flag);
        localStorage.setItem("nonPoFlag", nonPo_flag);
        localStorage.setItem("vendorEmail", vendorEmail ?? "");
        // GPXUSAP-1860: single boolean carried through from uiConfig (this SPA
        // otherwise discards the uiConfig block the impersonate response may
        // carry). Absent/garbage ⇒ "false", i.e. OFF. See
        // ImpersonationService for the consumer.
        localStorage.setItem(
            "adminUploadOnBehalf",
            String(adminUploadOnBehalf === true)
        );
    }

    ngOnInit(): void {
        const reqData = {
            supplierCode: this.code,
            session: {
                sessionId: this.sessionId,
            },
            sellerCode: this.sellerCode,
            username: this.username,
            sessionTimeout: this.sessionTimeout,
            expiryDate: this.expiryDate,
            lastLoginDate: this.lastLoginDate,
        };
        this.authService.impersonate(reqData).subscribe(
            (res) => {
                this.responseData = res;
                if (this.responseData.success) {
                    const user = this.responseData.data.user;
                    const vendorCode = user.eippSession.username;
                    const eippSession = user.eippSession.sessionId;
                    const sessionId = this.responseData.data.session.sessionId;
                    const displayName = user.displayName;
                    const sellerCode = user.sellerCode;
                    const nonPoFlag = user.eippSessionData.data.user.nonPoFlag;
                    const logisticFlag = user.eippSessionData.data.user.logisticsFlag;
                    const contact = user.eippSessionData.data.user.mobile;
                    const vendorEmail = user.eippSessionData.data.user.email;
                    // GPXUSAP-1860: uiConfig.adminUploadOnBehalf, read defensively.
                    // UNVERIFIED AGAINST THE BACKEND CONTRACT: this SPA has no
                    // established uiConfig response shape today (confirmed by
                    // grep — only nonPoFlag/logisticsFlag/mobile/email are ever
                    // pulled out of this response elsewhere in this repo), so
                    // both the top-level `user.uiConfig...` and the nested
                    // `eippSessionData.data.user.uiConfig...` paths below are
                    // GUESSES, not a confirmed BE contract. If neither matches
                    // what the backend actually sends, this silently stays OFF
                    // (safe failure mode) — check with the BE agent/contract
                    // before relying on this in QA/UAT. Anything but a literal
                    // `true` at either path is OFF.
                    const adminUploadOnBehalf =
                        user?.uiConfig?.adminUploadOnBehalf === true ||
                        user?.eippSessionData?.data?.user?.uiConfig
                            ?.adminUploadOnBehalf === true;
                    this.saveToken(
                        eippSession,
                        sessionId,
                        displayName,
                        vendorCode,
                        sellerCode,
                        nonPoFlag,
                        logisticFlag,
                        contact,
                        vendorEmail,
                        adminUploadOnBehalf
                    );
                    this.authService.setCurrentUser(user);
                    this.authenticationService.setCurrentUser(user);
                    localStorage.setItem("impersonation", "true");
                    localStorage.setItem("adminUser", this.adminName);
                    localStorage.setItem("adminEmail", this.adminEmail);
                    // GPXUSAP-1860 (4.4): when the `landing` query param was
                    // exactly "purchase-orders", land there instead of the
                    // default dashboard. Any other/absent value keeps today's
                    // behaviour unchanged. enforcePasswordChange still takes
                    // priority, exactly as before.
                    const targetUrl = user.eippSessionData.data.user.enforcePasswordChange
                        ? "/vendor-master-data"
                        : this.landing === "purchase-orders"
                        ? "/purchase-orders"
                        : "/dashboard";
                    this.jswVmd.fetchAndStoreForVendor(vendorCode).subscribe(() => {
                        this.router.navigateByUrl(targetUrl);
                    });
                }
            },
            (error) => {}
        );
    }
}
